import { isDevMode } from '@angular/core';
import {
    GridFiltersState,
    IGridParams,
    IUrlGrid,
} from '@pages/generics/grid/state-handlers/handler-types';
import { IDropdownOption } from '@uipath/angular/components/ui-grid';
import { ISortModel } from '@uipath/angular/components/ui-grid/models';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest/models';

import isEqual from 'lodash-es/isEqual';
import { Omit } from 'type-fest';

export class GridStateAdapter<TEntity> {
    constructor(private _grid: IUrlGrid<TEntity>) { }

    public setState(persistedState: IGridParams) {
        this._setSearch(persistedState.search);
        this._setSort(persistedState.sort);
        this._setFilters(persistedState.filters);

        this._grid.pageChange({
            ...this._grid.pageState,
            pageSize: persistedState.size,
            pageIndex: persistedState.index,
        });
    }

    private _setSearch(search?: string) {
        if (search === this._grid.header.searchValue) { return; }

        this._grid.filterManager.searchChange(search, this._grid.header);
    }

    private _setFilters(filterState: GridFiltersState = {}) {
        this._grid.columns
            .filter(column => !!column.dropdown || !!column.searchableDropdown)
            .forEach(column => {
                if (column.searchableDropdown) {
                    this._grid.filterManager.searchableDropdownUpdate(
                        column,
                        filterState[column.searchableDropdown.property!] as ISuggestValue,
                    );
                } else {
                    if (!column.dropdown!.items) {
                        throw new Error(`No items found for ${column.property}`);
                    }

                    const cachedOption = filterState[column.property as string] as IDropdownOption;
                    const dropdownOption = cachedOption ?
                        column.dropdown!.items.find(item => isEqual(item.value, cachedOption.value)) as IDropdownOption :
                        undefined;

                    this._grid.filterManager.dropdownUpdate(
                        column,
                        dropdownOption,
                    );
                }
            });
    }

    private _setSort(sort?: Omit<ISortModel<TEntity>, 'title'>) {
        if (
            !sort ||
            sort.direction == null ||
            sort.field == null
        ) { return; }

        const column = this._grid.columns.find(c => c.property === sort.field);
        if (!column) {
            if (isDevMode()) {
                console.error('Invalid sort:', sort);
            }
            return;
        }
        this._grid.sortManager.sort$.next(sort as ISortModel<TEntity>);
        column.sort = sort.direction;
    }
}
